// @flow
/* eslint pinterest/no-module-factory-load:0 */
import * as Cookie from 'app/packages/cookie-utils';
import AuthHandshake from 'app/common/lib/AuthHandshake';
import CONST from 'app/common/lib/Const';
import context from 'app/common/lib/context';
import contextEvent from 'app/common/lib/ContextEvent';
import contextLogger from 'app/common/lib/ContextLogger';
import getInitModuleName from 'app/common/lib/util/getInitModuleName';
import i18n from 'app/common/lib/i18n';
import isEmpty from 'lodash/isEmpty';
import Logging from 'app/common/lib/Logging';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import sendActiveUserPing from 'app/common/lib/react/sendActiveUserPing';
import storage from 'app/common/lib/Storage';
import templateConst from 'app/common/lib/templateConst';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { FacebookUserData } from 'app/packages/pinterest-flow-types/authentication';
import urlUtil from 'app/common/lib/urlUtil';
import user from 'app/common/lib/User';
import { formatBirthdayToUnixTime } from 'app/common/lib/react/dateUtils';
import { getSignupMethod } from 'app/packages/pinterest-authentication/registration';
import { getUserUnauthBehaviorDataMap } from 'app/common/lib/util/userBehaviorDataUtils';
import { unregisterServiceWorker } from 'app/common/lib/swUtil';
import type {
  FacebookSignupOptions,
  SignupContext,
  SignupOptions,
  RecaptchaSignupOptions,
} from 'app/packages/pinterest-flow-types/registration';

const facebookLoader = () =>
  import(/* webpackChunkName: "FacebookCommon" */ 'app/common/lib/Facebook');

const authHandshakeLog = Logging.getLogger('AuthHandshake');
const userRegistrationLog = Logging.getLogger('UserRegistration');

let _signupRequestAttempts = 0;

const trackCrossDomainRegister = (action, resourceOptions) => {
  trackRegisterAction(`web_cross_domain_register.${action}.${getSignupMethod(resourceOptions)}`);
};

export type RegistrationResourceOptions = {|
  ...SignupOptions,
  ...SignupContext,
  ...RecaptchaSignupOptions,
  visited_pages?: string,
  user_behavior_data?: string,
|};

const UserRegistration = {
  /**
   * Registers the user using the resource.
   *
   * @param {Object} signupOptions The options for the register resource
   * @return {!Promise} returns a deferred that resolves with the resource's data
   */
  register(signupOptions: RegistrationResourceOptions) {
    const resourceOptions: RegistrationResourceOptions = { ...signupOptions };
    const visitedPages = storage.localStorage.getItem(PCONST.LOCALSTORAGE_VISITED_PAGES);
    if (visitedPages) {
      resourceOptions.visited_pages = JSON.stringify(visitedPages);
    }

    // Add PLPs from shared pins
    const visitedPlps = storage.localStorage.getItem(PCONST.LOCALSTORAGE_VISITED_PLPS_BEFORE_LOGIN);
    if (visitedPlps) {
      const sharedPlps = [];
      for (const page of visitedPlps) {
        if (page.is_shared_pin) {
          sharedPlps.push({
            ts: page.ts,
            path: page.path,
            first_pin_image_signature: page.image_signature,
            is_shared: true,
          });
        }
      }
      if (sharedPlps) {
        resourceOptions.visited_pages = JSON.stringify(sharedPlps.concat(visitedPages || []));
      }
    }

    resourceOptions.user_behavior_data = getUserUnauthBehaviorDataMap();

    if (UserRegistration.shouldPerformCrossDomainRegister(resourceOptions)) {
      const handshakeDomains = templateConst.settings.CORS_HANDSHAKE_DOMAINS;
      if (handshakeDomains.indexOf(context.instance.origin) !== -1) {
        trackRegisterAction('unauth_cross_domain_register_debugging_counter');
        return UserRegistration.crossDomainRegister(resourceOptions);
      }
    }

    /** @type {IResource} */
    const resource = UserRegistration.getResource(resourceOptions);
    return resource
      .callCreate()
      .then(
        UserRegistration.onRegisterSuccess.bind(null, resourceOptions),
        UserRegistration.onRegisterError.bind(null, resourceOptions),
      );
  },

  crossDomainRegister(resourceOptions: RegistrationResourceOptions) {
    trackCrossDomainRegister('attempt', resourceOptions);
    return AuthHandshake.registerUser(resourceOptions).then(
      (response: { data?: string }) => {
        UserRegistration.onRegisterSuccess(resourceOptions, response);
        const token: string = response.data || '';
        if (token) {
          trackCrossDomainRegister('success_with_token', resourceOptions);
          return AuthHandshake.exchangeTokenAndSetSession(token, true).then(
            (response) => {
              trackCrossDomainRegister('success_token_exchanged', resourceOptions);
              authHandshakeLog.debug('Cross domain register successful');
            },
            (error) => {
              trackCrossDomainRegister('success_token_not_exchanged', resourceOptions);
              authHandshakeLog.error('Failed to set session for current user; ' + error.message);
              throw error;
            },
          );
        } else {
          trackCrossDomainRegister('success_without_token', resourceOptions);
          authHandshakeLog.error('No custom access token in cross domain register response');
          const errorMsg = i18n._(
            'We were able to create your account, but there was a problem ' +
              'logging you in. Try refreshing the page to continue.',
          );
          throw new Error(errorMsg);
        }
      },
      (error) => {
        trackCrossDomainRegister('failure', resourceOptions);
        authHandshakeLog.error('Failed to register new user; ' + error.message);
        UserRegistration.onRegisterError(resourceOptions, error);
      },
    );
  },

  onRegisterSuccess(resourceOptions: RegistrationResourceOptions, resourceResponse: Object) {
    unregisterServiceWorker('signup');
    Cookie.unset(PCONST.COOKIE_GPLUS_AUTO_LOGIN_DISABLED_API_ERROR);
    Cookie.unset(PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED_API_ERROR);
    userRegistrationLog.debug('Login, successful', resourceResponse);
    sendActiveUserPing(); // DO NOT REMOVE
    Cookie.set(PCONST.COOKIE_USER_JUST_REGISTERED, '1');
    UserRegistration.trackRegister(resourceOptions);
    return resourceResponse;
  },

  onRegisterError(
    resourceOptions: RegistrationResourceOptions,
    error: { api_error_code?: string },
  ) {
    if (error.api_error_code === CONST.API_ERROR_UNDERAGE_REGISTER_ATTEMPT) {
      // In case of an underage attempt, set the appropriate cookies to block further
      // signups, but let the caller to inspect and redirect as appropriate.
      // Cookie expiration set to 10 years.
      trackRegisterAction('multi_step_set_age_restrict_cookie');
      Cookie.set(PCONST.COOKIE_REGISTER_RESTRICT, '1', 24 * 365 * 10);
      Cookie.set(PCONST.COOKIE_PERSONALIZE_FROM_OFFSITE_BROWSING_COOKIE_NAME, 'disabled');
    }

    const isRetryableError =
      isEmpty(error.api_error_code) ||
      [CONST.API_SERVER_ERROR, CONST.API_REQUEST_TIMEOUT].includes(error.api_error_code);
    if (isRetryableError && _signupRequestAttempts < 3) {
      trackRegisterAction(
        `unauth_web.signup_retry_error.${String(error.api_error_code)}.${String(
          resourceOptions.container,
        )}`,
      );
      _signupRequestAttempts += 1;
      return UserRegistration.register(resourceOptions);
    }

    throw error;
  },

  trackRegister(resourceOptions: RegistrationResourceOptions) {
    let _contextEvent;
    switch (resourceOptions.container) {
      case 'home_page':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.HOME_PAGE_SIGNUP);
        trackRegisterAction('signup_home_page');
        break;
      case 'mweb_homepage':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.HOME_PAGE_SIGNUP);
        trackRegisterAction('signup_mweb_home_page');
        break;
      case 'plain_signup_modal':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.PLAIN_MODAL_SIGNUP);
        trackRegisterAction('signup_plain_modal');
        break;
      case 'closeup_signup_modal':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.CLOSEUP_MODAL_SIGNUP);
        trackRegisterAction('signup_closeup_modal');
        break;
      case 'widescreen_signup_modal':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.WIDESCREEN_MODAL_SIGNUP);
        trackRegisterAction('signup_widescreen_modal');
        break;
      case 'pinit_button_landing':
        _contextEvent = contextEvent.fromEventType(
          PCONST.thrift.EventType.PIN_IT_BUTTON_LANDING_SIGNUP,
        );
        trackRegisterAction('signup_pinit_button_landing');
        break;
      case 'inspired_banner':
        trackRegisterAction('signup_inspired_banner');
        break;
      case 'category_jump_banner':
        trackRegisterAction('signup_category_jump_banner');
        break;
      case 'send_pin_banner':
        _contextEvent = contextEvent.fromEventType(PCONST.thrift.EventType.SENT_PIN_LANDING_SIGNUP);
        trackRegisterAction('send_pin_banner');
        break;
      default:
        _contextEvent = contextEvent.fromEventType(
          PCONST.thrift.EventType.UNKNOWN_PLACEMENT_SIGNUP,
        );
        trackRegisterAction('signup_unknown_placement');
    }

    const container = resourceOptions.container || 'container_unknown';
    trackRegisterAction('signup.container.' + container);

    const signupSource = resourceOptions.signupSource || 'signupSource_unknown';
    trackRegisterAction('signup.source.' + signupSource);

    if (_contextEvent) {
      contextLogger.getInstance().addEvent(_contextEvent);
    }
    const referrer_url = document.referrer;
    let referrer;
    if (!referrer_url) {
      referrer = 'direct';
    } else if (referrer_url.indexOf('/t.co/') > -1) {
      referrer = 'twitter';
    } else if (referrer_url.indexOf('google.') > -1) {
      referrer = 'google';
    } else if (referrer_url.indexOf('bing.') > -1) {
      referrer = 'bing';
    } else if (referrer_url.indexOf('facebook.') > -1) {
      referrer = 'facebook';
    } else {
      referrer = 'other';
    }
    trackRegisterAction('signup_referrer.' + referrer);
    const referrer_module = urlUtil.getQueryStringParams().referrer || getInitModuleName();
    trackRegisterAction('signup_referrer_module.' + String(referrer_module));
    const userAgentPlatform = context.instance.user_agent_platform;
    if (userAgentPlatform === PCONST.USER_AGENT_PLATFORM_IOS) {
      trackRegisterAction('signup_mweb.iphone_web');
    } else if (userAgentPlatform === PCONST.USER_AGENT_PLATFORM_IPAD) {
      trackRegisterAction('signup_mweb.ipad_web');
    } else if (userAgentPlatform === PCONST.USER_AGENT_PLATFORM_ANDROID) {
      trackRegisterAction('signup_mweb.android_web');
    }
    const hybridTier = resourceOptions.hybridTier || 'tier_unknown';
    const page = resourceOptions.page || 'page_unknown';
    const method = getSignupMethod(resourceOptions);
    trackRegisterAction(
      `web_signup.${method}.success.${page}.${container}.${referrer}.${hybridTier}`,
    );
  },

  /**
   * @private
   */
  getResource(resourceOptions: RegistrationResourceOptions) {
    return ResourceFactory.create('UserRegisterResource', resourceOptions);
  },

  /**
   * Attempt to register with facebook
   *
   * @param {Object=} credentialsAndLoggingData
   * @return {!Promise}
   */
  attemptFacebookRegistration(
    credentialsAndLoggingData: FacebookSignupOptions | SignupContext,
    skipEmailCheck: boolean = false,
  ) {
    return UserRegistration.verifyFacebookEmailExists(skipEmailCheck).then((socialConnectData) => {
      if (!socialConnectData.email && !skipEmailCheck) {
        return Promise.reject({
          error: new Error('Email required to sign up.'),
          socialConnectData,
        });
      }

      const registerResourceOptions = UserRegistration.buildFacebookRegistrationResourceOptions(
        socialConnectData,
      );
      return UserRegistration.register({
        ...registerResourceOptions,
        ...credentialsAndLoggingData,
      });
    });
  },

  /**
   * Verify that the connected Facebook user's email field is available
   *
   * @return {!Promise}
   */
  verifyFacebookEmailExists(skipEmailCheck?: boolean) {
    return UserRegistration.fetchFacebookUserData().then((socialConnectData) => {
      if (!socialConnectData.email && !skipEmailCheck) {
        return Promise.reject({
          error: new Error('Email required to sign up.'),
          socialConnectData,
        });
      }

      return Promise.resolve(socialConnectData);
    });
  },

  /**
   * Build resource options from the social connect data
   *
   * @param {Object.<string,*>} socialConnectData The Facebook user's social data
   * @return {Object.<string,*>}
   */
  buildFacebookRegistrationResourceOptions(
    socialConnectData: FacebookUserData,
  ): FacebookSignupOptions {
    return {
      first_name: socialConnectData.first_name,
      last_name: socialConnectData.last_name,
      email: socialConnectData.email,
      gender: socialConnectData.gender,
      image_url: socialConnectData.image_url,
      facebook_id: socialConnectData.id,
      facebook_token: socialConnectData.access_token || '',
      birthday: String(formatBirthdayToUnixTime(socialConnectData.birthday || '')),
    };
  },

  /**
   * Request the authenticated Facebook user's personal data
   *
   * @return {?Promise.<!Object>} resolves with network data for the user.
   */
  fetchFacebookUserData() {
    return facebookLoader().then(
      ({ default: Facebook }) =>
        new Promise((resolve, reject) => {
          // $FlowFixMe Flow v0.104.0 upgrade supression.
          Facebook.ensureInit((_fb) => {
            _fb.getLoginStatus((response) => {
              if (!response.authResponse) {
                return reject(new Error('not authed'));
              }

              // Need fields other than the default, so list all fields wanted to make the request string.
              const fieldsWanted = [
                'picture.type(large)',
                'id',
                'first_name',
                'last_name',
                'email',
                'gender',
                'birthday',
              ];

              const requestString = '/me?fields=' + fieldsWanted.join(',');
              _fb.api(requestString, (response) => {
                if (!response || response.error) {
                  return reject(new Error('FB.api errored: ' + response.error));
                }

                // The condition below should always be true, though Facebook SDK does not state
                // this explicitly.
                if (
                  response.picture &&
                  response.picture.data &&
                  response.picture.data.url &&
                  !response.picture.data.is_silhouette
                ) {
                  response.image_url = response.picture.data.url;
                }
                return resolve(response);
              });
              return undefined;
            }, true); // means that it is forcing a network call to Facebook, TODO: possibly optimize this.
            // we don't always need to force a network call, but for some cases we do,
            // so generalizing for now. — Connor (June 6, 2013)
          }, user);
        }),
    );
  },

  shouldPerformCrossDomainRegister(resourceOptions: RegistrationResourceOptions) {
    // Cross-domain FB and G+ registration caused signup metrics drops
    // which were not resolved.
    if (
      resourceOptions.facebook_id ||
      resourceOptions.gplus_code ||
      resourceOptions.gplus_id_token ||
      resourceOptions.google_open_id_token
    ) {
      return false;
    }

    // Non-email registration endpoints do not support cross-domain registration
    if (!resourceOptions.email) {
      return false;
    }
    return true;
  },
};

export default UserRegistration;
